/* eslint-disable no-restricted-globals */
/* eslint-disable no-param-reassign */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import SyncSelect, { components } from 'react-select';
import { get, isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LoadingSpinner } from '../../LoadingSpinner';
import IndeterminateCheckbox from '../../IndeterminateCheckbox';
import { integerValueFields } from '../../../utils/shared';

import Toggle from './Toggle';

import './styles/MultiValueFilterOption.css';

const MultiValuePlaceholderActionItem = ({
  values,
  item: { value, label },
  func
}) => {
  const onRemove = e => {
    e.stopPropagation();
    func(values.filter(v => String(v.value) !== e.currentTarget.value));
  };
  return (
    <div className="tvt-filter__multi-value-placeholder-actionItem">
      {label}
      <button onClick={onRemove} value={value}>
        <FontAwesomeIcon icon="times" />
      </button>
    </div>
  );
};

const MultiValuePlaceholder = (
  showInlineSelections,
  func,
  isRequired
) => props => {
  const selectedCount = get(props, 'selectProps.value.length', 0);
  const placeholder = get(props, 'selectProps.placeholder', '');
  const selectedCountLabel = selectedCount ? `(${selectedCount})` : '';

  if (showInlineSelections && props.selectProps.value) {
    if (props.selectProps.value.length) {
      return (
        <div className="tvt-filter__placeholder">
          {props.selectProps.value.map(v => (
            <MultiValuePlaceholderActionItem
              values={props.selectProps.value}
              item={v}
              func={func}
            />
          ))}
        </div>
      );
    }
    return (
      <div className="tvt-filter__placeholder">
        {props.selectProps.placeholder}
      </div>
    );
  }

  return (
    <div className="tvt-filter__placeholder">
      <span className="tvt-filter__placeholder-label">{placeholder}</span>&nbsp;
      <span className="tvt-filter__placeholder-count">
        {selectedCountLabel}
      </span>
      {isRequired ? (
        <div className="tvt-filter__placeholder-label required">(Required)</div>
      ) : (
        ''
      )}
    </div>
  );
};

const MenuList = props => {
  const cmpts = get(props, ['selectProps', 'components'], {});

  const sanitizeBase = (v, id) => {
    if (integerValueFields.includes(id)) {
      return parseInt(v, 10);
    }
    return v;
  };

  const currentDropdownFilters = props.selectProps.id;
  const currentFieldStateValues =
    props.selectProps.fieldState &&
    props.selectProps.id in props.selectProps.fieldState &&
    props.selectProps.fieldState[props.selectProps.id].value
      ? props.selectProps.fieldState[props.selectProps.id].value
      : [];
  if (currentFieldStateValues) {
    currentFieldStateValues.reduce((acc, curr) => {
      const base = curr.value;
      if (!isNaN(curr.value)) {
        curr.value = curr.value.toString();
      }
      const isExcludedValue =
        curr.label.startsWith('Excludes:') || curr.value.startsWith('-');

      if (!isExcludedValue) {
        curr.state = 'included';
        curr.base = sanitizeBase(curr.value, props.selectProps.id);
      } else if (isExcludedValue) {
        if (integerValueFields.includes(currentDropdownFilters)) {
          curr.value = `not ${curr.value}`.replace('not not', 'not');
        } else {
          curr.value = `-${curr.value}`.replace('--', '-');
        }
        if (base.toString().includes('not')) {
          curr.base = parseInt(base.replace('not ', ''), 10);
        }
        if (base.toString().startsWith('-')) {
          curr.base = base.substring(1);
        }
        curr.state = 'excluded';
      } else {
        curr.state = 'empty';
      }

      acc[curr.value] = curr.state;

      return acc;
    }, {});
  }

  const currentFieldStateValuesLookup = currentFieldStateValues.reduce(
    (acc, curr) => {
      if (!isNaN(curr.base)) {
        curr.base = Number(curr.base);
      }
      acc[curr.base] = curr.state;
      return acc;
    },
    {}
  );
  let currentValues = currentFieldStateValues.map(v => ({
    ...v,
    state: currentFieldStateValuesLookup[v.value]
  }));
  if (
    currentValues.length < 1 &&
    props.selectProps &&
    props.selectProps.value != null
  ) {
    currentValues = props.selectProps.value;
  }

  const onChange = val => {
    let newOptions = [];
    if (currentDropdownFilters === 'season') {
      val.base = parseInt(val.base, 10);
    }
    newOptions = [
      ...currentValues.filter(
        obj => obj.base !== val.base && obj.state !== 'empty'
      ),
      val
    ];
    props.selectProps.onChange(newOptions);
  };

  if (!isEmpty(cmpts) && cmpts.BooleanSwitch) {
    return (
      <components.MenuList {...props}>
        <div>{cmpts.BooleanSwitch}</div>
        {props.children}
      </components.MenuList>
    );
  }

  if (get(props, ['children', 0, 'props', 'data', 'contentType'], false)) {
    return (
      <components.MenuList {...props}>
        {props.options.map(contentType => (
          <div key={contentType.contentType}>
            <div className="tvt-filter__options-section-header">
              <FontAwesomeIcon
                className="tvt-filter__options-section-header-icon"
                icon={contentType.contentType === 'shows' ? 'tv' : 'film'}
              />
              {contentType.contentType}
            </div>
            <div>
              {contentType.options.map((child, index) => (
                <>
                  <div className="tvt-filter__option tvt-filter-multi-title">
                    <IndeterminateCheckbox
                      {...props}
                      value={child.value}
                      label={child.label}
                      elementId={index}
                      onChange={onChange}
                    />
                    <div className="item-name">{child.label}</div>
                  </div>
                </>
              ))}
            </div>
          </div>
        ))}
      </components.MenuList>
    );
  }

  return (
    <>
      <components.MenuList {...props}>
        {props.children?.length &&
          props?.children?.map((child, index) => {
            const children = React.Children.toArray(child.props.children);
            children.unshift(
              <>
                <IndeterminateCheckbox
                  {...props}
                  value={child.props.value}
                  label={child.props.label}
                  hiddenValue={child.props.hiddenValue}
                  elementId={index}
                  onChange={onChange}
                  isIncludedExcluded={
                    currentFieldStateValuesLookup[child.props.value]
                  }
                />
                <div className="item-name">{child.props.label}</div>
              </>
            );
            const newChild = React.cloneElement(
              child,
              {
                newProp: 'value',
                existingProp: 'value',
                state: 'value'
              },
              children
            );
            return <>{newChild}</>;
          })}
      </components.MenuList>
    </>
  );
  // return <components.MenuList {...props}>{props.children}</components.MenuList>;
};

const BulkInputter = props => {
  const [loading, setLoading] = useState(false);
  const currentValues = get(props, ['selectProps', 'value'], []);
  const searchBulk = async e => {
    if (e.keyCode === 13) {
      const bulkArray = props.value.split('\n');
      if (bulkArray.length) {
        setLoading(true);
        const values = await props.selectProps.bulkRequest(bulkArray);
        await props.selectProps.onChange(
          isEmpty(values) ? null : [...values, ...currentValues]
        );
        setLoading(false);
      }
    }
  };
  return loading ? (
    <LoadingSpinner />
  ) : (
    <textarea
      {...props}
      ref={props.innerRef}
      onKeyDown={searchBulk}
      className="tvt-filter__bulk-input-text-area"
      rows={props.value.split('\n').length}
    />
  );
};

const MultiValueFilter = props => {
  const Select = props.isAsync ? AsyncSelect : SyncSelect;
  const [inputValue, setInputValue] = useState('');
  const onChange = val => {
    props.onChange(isEmpty(val) ? null : val);
  };

  const onInputChange = val => {
    setInputValue(val);
  };

  useEffect(() => {
    setInputValue('');
  }, [props.value]);

  const cmpnts = {
    ...get(props, 'components', {}),
    MenuList
  };

  if (props.allowBulk) {
    cmpnts.Input = BulkInputter;
  } else {
    cmpnts.Placeholder = MultiValuePlaceholder(
      props.showInlineSelections,
      onChange,
      props.isRequired
    );
  }

  return (
    <>
      <Select
        {...props}
        closeMenuOnSelect={false}
        components={cmpnts}
        controlShouldRenderValue={props.showInlineSelections}
        onChange={onChange}
        onInputChange={onInputChange}
        key={props.seasons}
        minMenuHeight={450}
        maxMenuHeight={450}
        isOptionDisabled={option => option.value !== ''}
        isMulti
        disallowExcluded={props.disallowExcluded}
        inputValue={inputValue}
      />
      {props.boolLayout === 'right' && props.hasBoolean && (
        <Toggle
          options={[
            { value: 'OR', label: 'OR' },
            { value: 'AND', label: 'AND' }
          ]}
          tooltip={props.tooltip}
          value={
            props.propsBoolean.checked === true
              ? 'OR'
              : props.propsBoolean.checked
          }
          {...props.propsBoolean}
        />
      )}
    </>
  );
};

MultiValuePlaceholderActionItem.propTypes = {
  values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  item: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  func: PropTypes.func.isRequired
};

MenuList.propTypes = {
  children: PropTypes.shape([])
};

MenuList.defaultProps = {
  children: []
};

MultiValueFilter.propTypes = {
  id: PropTypes.string.isRequired,
  isAsync: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any, // eslint-disable-line
  boolLayout: PropTypes.string,
  tooltip: PropTypes.string,
  propsBoolean: PropTypes.shape({
    label: PropTypes.string,
    labelRight: PropTypes.string,
    onChange: PropTypes.func
  }),
  hasBoolean: PropTypes.bool,
  showInlineSelections: PropTypes.bool,
  isRequired: PropTypes.bool
};

MultiValueFilter.defaultProps = {
  isAsync: false,
  isRequired: false,
  value: null,
  boolLayout: 'inside',
  tooltip: '',
  propsBoolean: {
    label: 'AND',
    labelRight: 'OR',
    onChange: () => {}
  },
  hasBoolean: false,
  showInlineSelections: false
};

export default MultiValueFilter;
